import type { RouteObject } from 'react-router-dom';

import DocsLayout from '@/app/layouts/DocsLayout/DocsLayout';
import MainLayout from '@/app/layouts/MainLayout';
import { AuthGuard, RoleGuard } from '@/app/router/guards';
import { lazyRoute } from '@/app/router/helpers';
import { ROUTES } from '@/shared/constants/app/routes.constant.ts';

/**
 * `/docs/*` — 도움말. 읽기 경로는 전부 공개, `/docs/manage/*` 만 ADMIN 이상이다.
 *
 * 본문은 core-service 의 jsonb 에 있다(대표님이 화면에서 직접 편집한다).
 * 트리는 한 번 받아 캐시하고, 본문은 문서를 열 때 문서 단위로 받는다.
 *
 * ── 레이아웃이 둘로 갈린다 ────────────────────────────────
 *   `/docs`, `/docs/manage/*`   → MainLayout (마케팅 헤더)
 *   `/docs/:section[/:slug]`    → DocsLayout (도움말 전용 헤더 + 3단 셸)
 *
 * 문서 상세만 헤더를 바꾼 이유는 메가메뉴다. 문서를 읽는 중에 마우스가 위로
 * 스치면 화면 절반이 메뉴로 덮이면서 sticky 사이드바·목차와 겹친다. 반대로
 * 도움말 첫 화면(`/docs`)은 사이트의 다른 랜딩과 같은 층이라 마케팅 헤더가 맞다.
 */
export const docsRoutes: RouteObject[] = [
  {
    path: ROUTES.DOCS,
    Component: MainLayout,
    children: [
      {
        index: true,
        ...lazyRoute(() => import('@/pages/docs/DocsIndexPage')),
      },
      // ── 관리 (ADMIN 이상) ────────────────────────────────
      // 3단 셸 밖에 둔다 — 관리 화면은 사이드바 없이 전체 폭을 쓴다.
      // `manage` 가 `:section` 보다 먼저 선언돼야 읽는 사람이 우선순위를 오해하지 않는다
      // (React Router 는 선언 순서와 무관하게 정적 세그먼트를 우선 매칭한다).
      {
        element: <AuthGuard />,
        children: [
          {
            element: <RoleGuard allowedRoles={['ADMIN', 'SUPER_ADMIN']} />,
            children: [
              {
                path: ROUTES.DOCS_MANAGE_NEW,
                ...lazyRoute(
                  () => import('@/pages/docs/manage/DocsDocFormPage'),
                ),
              },
              {
                path: ROUTES.DOCS_MANAGE_EDIT,
                ...lazyRoute(
                  () => import('@/pages/docs/manage/DocsDocFormPage'),
                ),
              },
              {
                path: ROUTES.DOCS_MANAGE,
                ...lazyRoute(() => import('@/pages/docs/manage/DocsManagePage')),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    // 상세 두 경로만 도움말 전용 셸을 공유한다.
    path: ROUTES.DOCS,
    Component: DocsLayout,
    children: [
      {
        path: ROUTES.DOCS_SECTION,
        ...lazyRoute(() => import('@/pages/docs/DocsSectionPage')),
      },
      {
        path: ROUTES.DOCS_ARTICLE,
        ...lazyRoute(() => import('@/pages/docs/DocsArticlePage')),
      },
    ],
  },
];
